import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./ProductsInvoice.css";
import { useAuth } from "../../auth/useAuth";

const ProductsInvoice = ({ userId: propUserId }) => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  
  const [searchParams] = useSearchParams();

  const { user } = useAuth();

  useEffect(() => {
    const encodedData = searchParams.get("paymentData");
    if (encodedData) {
      try {
        const decodedOrder = JSON.parse(atob(decodeURIComponent(encodedData)));
        setPaymentDetails(decodedOrder);
        return;
      } catch (e) {
        console.error("Failed to decode paymentData", e);
      }
    }

    const fetchLatest = async () => {
      const id = propUserId || searchParams.get("userId") || user?.user_id;
      if (!id) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/order/order-list/${id || user.user_id}`);
        const data = await response.json();
        const latest = Array.isArray(data.orders) ? data.orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] : null;
        setPaymentDetails(latest);
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };

    fetchLatest();
  }, [propUserId, searchParams, user?.user_id]);

  const items = paymentDetails?.items || [];

  const now = paymentDetails?.created_at ? new Date(paymentDetails.created_at) : new Date();

  const formattedDate = now
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    })
    .toUpperCase();

  const formattedTime = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const renderPaymentInfo = () => {
    const method = paymentDetails?.payment_method;

    switch (method) {
      case "card":
        return (
          <>
            <p className="inv-para-bold">METHOD <span className="colon-card-method">:</span> CARD</p>
            <p>Network <span className="colon-card-netbank-method">:</span> {paymentDetails.card_network?.toUpperCase()}</p>
            <p>Card <span className="colon-card-number">:</span> **** **** **** {paymentDetails.card_last4}</p>
          </>
        );
      case "upi":
        return (
          <>
            <p className="inv-para-bold">METHOD: UPI</p>
            <p>VPA: {paymentDetails.vpa}</p>
          </>
        );
      case "netbanking":
        return (
          <>
            <p className="inv-para-bold">METHOD <span className="colon-bank-method">:</span> NET BANKING</p>
            {/* <p>Bank <span className="colon-bank-name">:</span> {paymentDetails.bank?.replace(/_/g, " ").toUpperCase()}</p> */}
          </>
        );
      case "wallet":
        return (
          <>
            <p className="inv-para-bold">METHOD: WALLET</p>
            <p>Provider: {paymentDetails.wallet?.toUpperCase()}</p>
          </>
        );
      default:
        return (
          <>
            <p className="inv-para-bold">METHOD: {method?.toUpperCase() || "ONLINE"}</p>
            <p>Ref: {paymentDetails?.payment_id}</p>
          </>
        );
    }
  };

  if (!paymentDetails) {
    return <div className="product-invoice-container"><p>No payment details found.</p></div>;
  }

  return (
    <div className="product-invoice-container">
      {/* Geometric Accents */}
      <div className="top-right-accent-container">
        <div className="top-right-pink-block"></div>
        <div className="top-right-teal-block"></div>
      </div>
      <div className="bottom-left-teal"></div>
      <div className="bottom-left-pink"></div>

      <div className="invoice-content">
        <header className="header-main">
          <h1 className="title-large">INVOICE</h1>
        </header>

        <section className="billing-details">
          <div className="billing-col-inv">
            <h4>ISSUED TO:</h4>
            {/* No <strong> — Barlow bold via CSS class */}
            <p className="inv-para-bold">{paymentDetails?.name || "Customer"}</p>
            <p>{paymentDetails?.email}</p>
            <p>+91 {paymentDetails?.mobile}</p>
          </div>
          <div className="billing-col-inv">
            <h4>PAYMENT DETAILS:</h4>
            {renderPaymentInfo()}
            <p>Order ID <span className="colon-order-id">:</span> {paymentDetails?.razorpay_order_id}</p>
            <p>Payment ID: {paymentDetails?.payment_id}</p>
          </div>
        </section>

        <section className="invoice-meta-strip">
          <div className="meta-item">
            {/* No <strong> — plain Barlow */}
            <p>INVOICE NO : {paymentDetails?.invoice_id || "PENDING"}</p>
          </div>
          <div className="meta-item">
            <p>DATE <span className="colon-date">:</span> {formattedDate}</p>
            <p className="time-p-inv">TIME <span className="colon-time"> : </span>{formattedTime}</p>
          </div>
        </section>

        <table className="product-table">
          <thead>
            <tr>
              <th>PRODUCT</th>
              <th>UNIT PRICE</th>
              <th>MARSMATE PRICE</th>
              <th>QTY</th>
              <th>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>{item.name}</td>
                <td>{Number(item.original_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td>{Number(item.discounted_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td>{item.quantity}</td>
                <td>{Number(item.total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan="2">TOTAL</td>
              <td></td>
              <td>{paymentDetails.quantity}</td>
              <td>{Number(paymentDetails?.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tfoot>
        </table>

        <footer className="footer-sign">
          <div className="thanks-note">
            <h3>Thank you for Choosing MarsMate AI!</h3>
          </div>
          <div className="signature-area">
            <div className="sig-image">
              <img
                src="/image/Signature.png"
                alt="Signature"
                style={{ width: "150px", height: "70px", objectFit: "contain" }}
              />
            </div>
            <div className="sig-line"></div>
            <p>Founder</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ProductsInvoice;